import HiddenInput from "@/components/form/components/HiddenInput";
import { InputNumber } from "@/components/InputNumber";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Input, Modal, Row } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateManualDebtAccountMovement,
  useUpdateManualDebtAccountMovement,
} from "../../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { UUID } from "../../../../../typings/global";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { CommissionsCategory } from "../../../enums";
import { CreateUpdateDebtAccountMovement, DebtAccountMovement } from "../../types";
import { convertDebtAccountMovementToCreateUpdateObject } from "../../utils";

type Props = {
  agentId: UUID;
  isOpen: boolean;
  onCancel: () => void;
  movement?: DebtAccountMovement;
  category: CommissionsCategory;
};

export const DebtAccountMovementModalForm = ({ isOpen, agentId, onCancel, movement, category }: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const [form] = Form.useForm<CreateUpdateDebtAccountMovement>();

  const createManualDebtAccountMovement = useCreateManualDebtAccountMovement();
  const updateManualDebtAccountMovement = useUpdateManualDebtAccountMovement();

  useEffect(() => {
    if (isOpen && movement) {
      const formData = convertDebtAccountMovementToCreateUpdateObject(movement);

      form.setFieldsValue(formData);
    }
  }, [isOpen, movement, form]);

  const title = useMemo(() => {
    let text = movement
      ? t("commissions.debtAccount.titles.updateMovement")
      : t("commissions.debtAccount.titles.createMovement");

    if (isTopAgentPlus) {
      text += ` ${t(`commissions.enums.commissionsCategory_PLUS.${category}`)}`;
    }

    return text;
  }, [category, isTopAgentPlus, movement]);

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (movement) {
        updateManualDebtAccountMovement.mutate(
          { id1: agentId, id2: movement.id, object: values },
          {
            onSuccess: onCancel,
            onError: (error) => setErrorsToForm(form, "commissions.debtAccount.attrs.movements", error.violations),
          },
        );
      } else {
        createManualDebtAccountMovement.mutate(
          { id: agentId, object: values },
          {
            onSuccess: onCancel,
            onError: (error) => setErrorsToForm(form, "commissions.debtAccount.attrs.movements", error.violations),
          },
        );
      }
    });
  };

  return (
    <Modal
      width={ModalSizes.SMALL}
      open={isOpen}
      title={title}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={false}
      mask={{ closable: false }}
      afterClose={() => form.resetFields()}
      onCancel={onCancel}
      onOk={handleSubmit}
    >
      <Form form={form} layout="vertical" name={`createUpdateDebtAccountMovement${category}`}>
        <HiddenInput name="optimisticLockVersion" />
        <HiddenInput name="category" initialValue={category} />

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="amount"
              label={t("commissions.debtAccount.attrs.movements.amount")}
              rules={[validations.notNull, validations.positiveOrNegativeNumber]}
            >
              <InputNumber type="decimal" addon="euro" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="note"
              label={t("commissions.debtAccount.attrs.movements.note")}
              rules={[validations.size(1, 8192)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
